import React, { useRef } from 'react';

import { Builder, BuilderBlocks } from '@builder.io/react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FlyOutMenu from '~/builder/components/header/components/FlyOutMenu';
import PromoTrackingWrapper from '~/builder/components/PromoTrackingWrapper/PromoTrackingWrapper';
import { createDataAutoTagString } from '~/shared/utils/createDataAutoTagString';
import { getBuilderDataAnalyticsProps } from '~/shared/utils/getBuilderDataAnalyticsProps';
import { Router } from '~/techstyle-shared/next-routes';
import { FABLETICS_BUILDER_ENUMS } from '~/techstyle-shared/react-builder-models';
import { useReferringPage } from '~/techstyle-shared/react-marketing';

const NAV_CONTENT_COLORS = FABLETICS_BUILDER_ENUMS.NAV_CONTENT_COLORS;

const TabLabel = styled.span`
  position: relative;
  padding: 19px 0px;

  &::after {
    background-color: ${({ theme }) => theme.colors.headNavTabActive};

    ${({ $labelStyle, theme }) =>
      $labelStyle &&
      $labelStyle !== NAV_CONTENT_COLORS.DEFAULT &&
      theme.builderHeader.navTabsListWithFlyoutMenu.tabLabel
        .customBackgroundColors[$labelStyle]};
  }

  ${({ theme }) => theme.builderHeader.navTabsListWithFlyoutMenu.tabLabel.base}
`;

const TabLink = styled(FlyOutMenu.UnstyledToggle)`
  font-family: ${({ theme }) => theme.fontStacks.baseFamily};
  color: inherit;
  background: none;
  border: none;
  border-radius: 0;
  cursor: pointer;

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.builderHeader.navTabsListWithFlyoutMenu.tabLink}

  ${TabLabel} {
    white-space: nowrap;

    color: ${({ theme }) => theme.colors.headNavTabInactive};

    ${({ $labelStyle, theme }) =>
      $labelStyle &&
      $labelStyle !== NAV_CONTENT_COLORS.DEFAULT &&
      theme.builderHeader.customColors[$labelStyle]};
  }

  &[aria-expanded='true'],
  &:focus-visible {
    ${TabLabel} {
      color: ${({ theme }) => theme.colors.headNavTabActive};

      ${({ $labelStyle, theme }) =>
        $labelStyle &&
        $labelStyle !== NAV_CONTENT_COLORS.DEFAULT &&
        theme.builderHeader.customColors[$labelStyle]};

      &::after {
        width: 100%;
        left: 0;
      }

      ${({ theme }) =>
        theme.builderHeader.navTabsListWithFlyoutMenu.tabLabel.focus}
    }
  }

  @media (max-width: 1439px) {
    ${TabLabel} {
      font-size: 14px;
    }
  }

  @media (max-width: 1119px) {
    ${TabLabel} {
      font-size: 12px;
    }
  }
`;

const Popup = styled(FlyOutMenu.Popup).attrs({ forwardedAs: 'nav' })`
  left: 0;
  right: 0;
  z-index: 4;
`;

const Wrapper = styled.ul`
  display: flex;
  flex-direction: row;
  list-style-type: none;
  width: 100%;

  ${({ theme }) => theme.builderHeader.navTabsListWithFlyoutMenu.wrapper}
`;

const Content = styled.div`
  position: absolute;
  z-index: 4;
  width: 100vw;
  background-color: ${({ theme: { colors } }) =>
    colors.headNavFlyoutBackground};
  border-bottom: solid ${({ theme: { colors } }) => colors.headNavFlyoutBorder}
    1px;

  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  column-gap: 64px;

  /* Produces a backdrop on everything below the menu */
  box-shadow: 0 5000px 0 5000px rgba(51, 51, 51, 0.4);

  ${({ theme }) => theme.builderHeader.navTabsListWithFlyoutMenu.content}
`;

const DataAnalyticsShape = {
  category: PropTypes.string,
  label: PropTypes.string,
};

const TabShape = {
  columns: PropTypes.array,
  dataAnalytics: PropTypes.shape(DataAnalyticsShape),
  isOn: PropTypes.bool,
  tabLabelColor: PropTypes.string,
  textLabel: PropTypes.string,
  textUrl: PropTypes.string,
};

function Tab({ builderBlock, tab, tabIndex }) {
  const { setReferringPage } = useReferringPage();
  const popupRef = useRef();

  if (!tab) {
    return null;
  }

  const isOn = Builder.isEditing && tab.isOn;

  function handleClick() {
    setReferringPage(tab.analyticsLabel);
    Router.push(tab.textUrl);
  }

  return (
    <FlyOutMenu.Parent isOn={isOn} noDelay>
      <TabLink
        $labelStyle={tab.tabLabelColor}
        onClick={handleClick}
        isTouchEnabled
        aria-label={tab.textLabel}
        data-autotag={createDataAutoTagString('header-tab', tab.textLabel)}
        {...getBuilderDataAnalyticsProps(tab.dataAnalytics)}
      >
        <TabLabel $labelStyle={tab.tabLabelColor}>{tab.textLabel}</TabLabel>
      </TabLink>
      <Popup ref={popupRef}>
        <Content $columns={tab.columns.length}>
          {tab.columns?.map((column, columnIndex) => (
            <PromoTrackingWrapper key={columnIndex} root={popupRef.current}>
              <BuilderBlocks
                key={columnIndex}
                parentElementId={builderBlock && builderBlock.id}
                dataPath={`component.options.tabs.${tabIndex}.columns.${columnIndex}.content`}
                blocks={column.content || column}
              />
            </PromoTrackingWrapper>
          ))}
        </Content>
      </Popup>
    </FlyOutMenu.Parent>
  );
}

Tab.propTypes = {
  builderBlock: PropTypes.any,
  tab: PropTypes.shape(TabShape),
  tabIndex: PropTypes.number,
};

function BuilderNavTabsListWithFlyoutMenu({ builderBlock, tabs }) {
  return (
    <Wrapper data-builder-component-type="nav_tabs_list_with_flyout_menu">
      {tabs.map((tab, index) => (
        <Tab
          key={index}
          builderBlock={builderBlock}
          tab={tab}
          tabIndex={index}
        />
      ))}
    </Wrapper>
  );
}

BuilderNavTabsListWithFlyoutMenu.propTypes = {
  builderBlock: PropTypes.any,
  tabs: PropTypes.arrayOf(PropTypes.shape(TabShape)),
};

export default BuilderNavTabsListWithFlyoutMenu;
